
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Plus, Trash2, FileVideo, Upload,
    ChevronDown, ChevronUp,
    ArrowRight, Music, X, Check, Loader2, GripVertical,
    Sparkles, Layers, Type, Info, Save, Settings, Youtube
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ProjectService } from '../services/ProjectService';
import { YouTubeUploadModal } from '../components/YouTubeUploadModal';
import { PreviewCanvas } from '../components/ranking/PreviewCanvas';
import { CompositeTimeline } from '../components/ranking/CompositeTimeline';
import { RichTextTitleEditor } from '../components/RichTextTitleEditor';
import { TRANSITION_TYPES } from '../services/TransitionManager';
import './VideoRankingPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Font options - using fonts that are available locally in backend/downloads/fonts
const FONT_OPTIONS = [
    { id: 'lato', label: 'Lato', family: '"Lato", sans-serif' },
    { id: 'archivo-black', label: 'Archivo Black', family: '"Archivo Black", Impact, sans-serif' },
    { id: 'impact', label: 'Impact', family: '"Impact", sans-serif' },
    { id: 'montserrat', label: 'Montserrat', family: '"Montserrat", sans-serif' },
    { id: 'bartle', label: 'Bartle', family: '"Bartle", sans-serif' },
    { id: 'bogle', label: 'Bogle', family: '"Bogle", sans-serif' },
];

const CUSTOM_FONTS_CONFIG = [
    { family: 'Lato', prefix: 'lato', weights: [700, 900] },
    { family: 'Archivo Black', prefix: 'archivo-black', weights: [700, 900] },
    { family: 'Impact', prefix: 'impact', weights: [400, 700, 900] },
    { family: 'Montserrat', prefix: 'montserrat', weights: [400, 700, 900] },
    { family: 'Bartle', prefix: 'bartle', weights: [400, 700, 900] },
    { family: 'Bogle', prefix: 'bogle', weights: [400, 700, 900] },
];

// --- Sub-components ---

const StyleControls = ({ values, onChange, title = "Appearance" }) => {
    return (
        <div className="style-controls">
            <div className="style-group-title">{title}</div>

            <div className="control-row">
                <div className="control-item full-width">
                    <label>Font</label>
                    <div className="select-wrapper">
                        <select
                            value={values.font}
                            onChange={(e) => onChange('font', e.target.value)}
                        >
                            {FONT_OPTIONS.map(font => (
                                <option key={font.id} value={font.id}>{font.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="control-row">
                <div className="control-item full-width">
                    <label>Animation</label>
                    <div className="select-wrapper">
                        <select
                            value={values.animation || 'none'}
                            onChange={(e) => onChange('animation', e.target.value)}
                        >
                            <option value="none">None</option>
                            <option value="fadeIn">Fade In</option>
                            <option value="fadeInUp">Fade In Up</option>
                            <option value="popIn">Pop In</option>
                            <option value="slideInRight">Slide In Right</option>
                            <option value="bounceIn">Bounce In</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="control-row">
                <div className="control-item full-width">
                    <label>Font Size: {values.fontSize || 48}px</label>
                    <input
                        type="range"
                        min="16"
                        max="120"
                        value={values.fontSize || 48}
                        onChange={(e) => onChange('fontSize', parseInt(e.target.value))}
                        className="cyan-slider"
                    />
                </div>
            </div>

            <div className="control-row">
                <div className="control-item full-width">
                    <label>Max Width: {values.maxWidth || 90}%</label>
                    <input
                        type="range"
                        min="30"
                        max="100"
                        value={values.maxWidth || 90}
                        onChange={(e) => onChange('maxWidth', parseInt(e.target.value))}
                        className="cyan-slider"
                    />
                    <span className="control-hint">Controls text line wrapping</span>
                </div>
            </div>

            <div className="control-row">
                <div className="control-item">
                    <label>Color</label>
                    <div className="color-input-wrapper">
                        <input
                            type="color"
                            value={values.color}
                            onChange={(e) => onChange('color', e.target.value)}
                        />
                        <input
                            type="text"
                            value={values.color}
                            onChange={(e) => onChange('color', e.target.value)}
                            className="hex-input"
                        />
                    </div>
                </div>

                <div className="control-item">
                    <label>Stroke</label>
                    <div className="color-input-wrapper">
                        <input
                            type="color"
                            value={values.strokeColor}
                            onChange={(e) => onChange('strokeColor', e.target.value)}
                        />
                        <input
                            type="text"
                            value={values.strokeColor}
                            onChange={(e) => onChange('strokeColor', e.target.value)}
                            className="hex-input"
                        />
                    </div>
                </div>
            </div>

            <div className="control-row">
                <div className="control-item full-width">
                    <label>Stroke Width: {values.strokeWidth}px</label>
                    <input
                        type="range"
                        min="0"
                        max="20"
                        value={values.strokeWidth}
                        onChange={(e) => onChange('strokeWidth', parseInt(e.target.value))}
                        className="cyan-slider"
                    />
                </div>
            </div>

            <div className="control-row">
                <div className="control-item full-width">
                    <label>Stroke Opacity: {values.strokeOpacity !== undefined ? values.strokeOpacity : 100}%</label>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={values.strokeOpacity !== undefined ? values.strokeOpacity : 100}
                        onChange={(e) => onChange('strokeOpacity', parseInt(e.target.value))}
                        className="cyan-slider"
                    />
                </div>
            </div>
        </div>
    );
};

const VideoTimeline = ({ startTime, endTime, duration, onChange }) => {
    const [isDragging, setIsDragging] = useState(null);
    const containerRef = useRef(null);

    const handlePointerDown = (type) => (e) => {
        // Pointer events work for mouse + touch + pen (mobile-friendly)
        setIsDragging(type);
        e.preventDefault();
        e.stopPropagation();
    };

    const handleMouseMoveRef = useRef();

    handleMouseMoveRef.current = (e) => {
        if (!isDragging || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const percentage = x / rect.width;
        const time = Math.round(percentage * duration * 10) / 10;

        if (isDragging === 'start') {
            const newStart = Math.min(time, endTime - 0.5);
            onChange('startTime', Math.max(0, newStart));
        } else if (isDragging === 'end') {
            const newEnd = Math.max(time, startTime + 0.5);
            onChange('endTime', Math.min(duration, newEnd));
        }
    };

    useEffect(() => {
        const handleMove = (e) => handleMouseMoveRef.current(e);
        const handleUp = () => setIsDragging(null);

        if (isDragging) {
            // Use pointer events so drag works on mobile (touch) and desktop.
            window.addEventListener('pointermove', handleMove, { passive: false });
            window.addEventListener('pointerup', handleUp);
            window.addEventListener('pointercancel', handleUp);
        }
        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
        };
    }, [isDragging]);

    const leftPct = (startTime / duration) * 100;
    const rightPct = (endTime / duration) * 100;
    const widthPct = rightPct - leftPct;

    return (
        <div className="timeline-control">
            <div className="timeline-header">
                <label className="control-label">Timeline Range</label>
                <div className="time-badges">
                    <span className="time-badge start">{startTime.toFixed(1)}s</span>
                    <span className="time-badge end">{endTime.toFixed(1)}s</span>
                </div>
            </div>
            <div className="timeline-track-container" ref={containerRef}>
                <div className="timeline-scale">
                    {[...Array(11)].map((_, i) => (
                        <div key={i} className="tick"></div>
                    ))}
                </div>
                <div className="timeline-bar-bg"></div>
                <div
                    className="timeline-selection"
                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                >
                    <div
                        className="timeline-handle handle-left"
                        onPointerDown={handlePointerDown('start')}
                    >
                        <div className="handle-grip"></div>
                    </div>
                    <div
                        className="timeline-handle handle-right"
                        onPointerDown={handlePointerDown('end')}
                    >
                        <div className="handle-grip"></div>
                    </div>
                </div>
            </div>
            <div className="time-total">Total Duration: {duration.toFixed(1)}s</div>
        </div>
    );
};

const VideoItemCard = ({ video, index, onChange, onRemove, isExpanded, onToggleExpand, session, dragHandleProps, videoQuality }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleUrlSubmit = async () => {
        if (!video.url) return;
        setIsLoading(true);
        setError(null);

        try {
            const token = session?.access_token;
            if (!token) throw new Error("Please log in to fetch video info");

            const res = await fetch(`${API_BASE}/api/ranking/extract-info`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ url: video.url, video_quality: videoQuality })
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.error || "Failed to fetch video info");

            // Construct full preview URL if server provided one
            let previewUrl = video.url;
            if (data.preview_url) {
                // Server-proxied preview - prepend API base URL
                previewUrl = data.preview_url.startsWith('/')
                    ? `${API_BASE}${data.preview_url}`
                    : data.preview_url;
            }

            onChange(video.id, {
                ...video,
                previewUrl: previewUrl,
                thumbnail: data.thumbnail_url,
                duration: data.duration || 60,
                endTime: data.duration || 60, // Use full video duration by default
                label: data.title ? data.title.substring(0, 30) : video.label,
                isLoaded: true,
                platform: data.platform,
                videoId: data.video_id
            });

        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileUpload = (file) => {
        if (!file) return;
        const objectUrl = URL.createObjectURL(file);
        const videoEl = document.createElement('video');
        videoEl.preload = 'metadata';
        videoEl.onloadedmetadata = () => {
            onChange(video.id, {
                ...video,
                file: file,
                previewUrl: objectUrl,
                duration: videoEl.duration,
                endTime: videoEl.duration, // Use full video duration by default
                isLoaded: true
            });
        };
        videoEl.src = objectUrl;
    };

    const updateStyle = (key, value) => {
        onChange(video.id, {
            ...video,
            style: { ...video.style, [key]: value }
        });
    };

    const handleTimelineChange = (key, value) => {
        onChange(video.id, { ...video, [key]: value });
    };

    return (
        <div className={`video-input-card ${isExpanded ? 'expanded' : ''}`}>
            <div className="card-header" onClick={onToggleExpand}>
                <div className="header-left">
                    <div className="drag-handle" {...dragHandleProps}>
                        <GripVertical size={16} />
                    </div>
                    <div className="header-info">
                        <span className="video-label">{video.label || `Video ${index + 1}`}</span>
                        {video.isLoaded && <span className="timestamp-preview">{video.startTime.toFixed(1)}s - {video.endTime.toFixed(1)}s</span>}
                    </div>
                </div>
                <div className="card-actions">
                    <button className="icon-btn">{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onRemove(video.id); }}
                        className="icon-btn delete-btn"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="card-content">
                    {!video.isLoaded ? (
                        <div className="input-options">
                            {error && <div className="error-msg">{error}</div>}
                            <div className="url-input-container">
                                <FileVideo size={18} className="input-icon" />
                                <input
                                    type="text"
                                    placeholder="TikTok, Instagram, or YouTube link"
                                    value={video.url}
                                    onChange={(e) => onChange(video.id, { ...video, url: e.target.value })}
                                    onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                                />
                                <button className="import-btn" onClick={handleUrlSubmit} disabled={isLoading || !video.url}>
                                    {isLoading ? <Loader2 size={16} className="spin" /> : <ArrowRight size={16} />}
                                </button>
                            </div>
                            <div className="divider"><span>Or</span></div>
                            <div className="file-upload-container">
                                <input
                                    type="file"
                                    id={`file-${video.id}`}
                                    className="hidden-file-input"
                                    accept="video/*"
                                    onChange={(e) => handleFileUpload(e.target.files[0])}
                                />
                                <label htmlFor={`file-${video.id}`} className="file-upload-label">
                                    <Upload size={18} />
                                    <span>Upload MP4</span>
                                </label>
                            </div>
                        </div>
                    ) : (
                        <div className="loaded-video-controls">
                            <div className="video-status-bar">
                                <div className="status-info">
                                    <span className="status-tag success"><Check size={12} /> Loaded</span>
                                    <span className="file-name">{video.url || video.file?.name}</span>
                                </div>
                                <button className="text-btn danger" onClick={() => onChange(video.id, { ...video, isLoaded: false, previewUrl: null })}>
                                    <X size={14} /> Remove
                                </button>
                            </div>

                            <VideoTimeline
                                startTime={video.startTime}
                                endTime={video.endTime}
                                duration={video.duration}
                                onChange={handleTimelineChange}
                            />
                        </div>
                    )}

                    <div className="video-text-editor">
                        <label className="section-label">Video Text Overlay</label>
                        <input
                            type="text"
                            value={video.label}
                            onChange={(e) => onChange(video.id, { ...video, label: e.target.value })}
                            className="fancy-input"
                            placeholder="e.g. Best Moment"
                        />

                        <StyleControls
                            values={video.style}
                            onChange={updateStyle}
                            title="Text Appearance"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};


// --- Main Page ---

export const VideoRankingPage = ({ session, refreshUsage }) => {
    // Rich text title HTML content
    const [titleHtml, setTitleHtml] = useState('<span style="color: #FFFFFF;">Top Funniest</span><br><span style="color: #06B6D4;">Moments</span>');

    const [titleStyle, setTitleStyle] = useState({
        font: 'archivo-black',
        strokeColor: '#000000',
        strokeWidth: 0,
        strokeOpacity: 100,
        fontSize: 80,
        maxWidth: 90,
        verticalPosition: 5,
        animation: 'fadeInUp',
        textAlign: 'center'
    });

    // Project State
    const [searchParams] = useSearchParams();
    const [projectId, setProjectId] = useState(searchParams.get('project_id'));
    const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved
    const [lastGeneratedPath, setLastGeneratedPath] = useState(null); // Supabase path of last generated video
    const [currentJobId, setCurrentJobId] = useState(null); // Store job ID to associate with storage

    // YouTube Upload Modal
    const [showYoutubeModal, setShowYoutubeModal] = useState(false);

    // Audio State
    const [audioFile, setAudioFile] = useState(null);
    const [audioFileName, setAudioFileName] = useState('');
    const audioInputRef = useRef(null);

    // Transition settings
    const [transitionType, setTransitionType] = useState('fade');
    const [transitionDuration, setTransitionDuration] = useState(0.5);

    // Video quality setting (determines preview and export quality)
    const [videoQuality, setVideoQuality] = useState('720');

    // Preview state
    const previewRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [totalDuration, setTotalDuration] = useState(0);
    const [lastDownloadUrl, setLastDownloadUrl] = useState(null);

    // Load project if ID exists
    // Load project if ID exists
    useEffect(() => {
        const loadProject = async (id) => {
            try {
                const project = await ProjectService.getProject(session.access_token, id);
                if (project && project.data) {
                    const data = project.data;
                    // Restore state from project data
                    if (data.videos) setVideos(data.videos);
                    if (data.titleHtml) setTitleHtml(data.titleHtml);
                    if (data.titleStyle) setTitleStyle(data.titleStyle);
                    if (data.transitionType) setTransitionType(data.transitionType);
                    if (data.transitionDuration) setTransitionDuration(data.transitionDuration);
                    if (data.audioFileName) setAudioFileName(data.audioFileName);
                    if (data.supabase_path) setLastGeneratedPath(data.supabase_path);
                    if (data.download_url || data.video_url) setLastDownloadUrl(data.download_url || data.video_url);
                    if (data.job_id) setCurrentJobId(data.job_id); // Restore job ID
                    if (data.videoQuality) setVideoQuality(data.videoQuality); // Restore video quality
                    // Note: We cannot restore the actual audio file object, user might need to re-upload
                    // But we can keep the name so they know.
                }
            } catch (err) {
                console.error("Failed to load project:", err);
                // Clear projectId so save creates a new project instead of trying to update
                setProjectId(null);
                // Clear URL param
                const newUrl = new URL(window.location);
                newUrl.searchParams.delete('project_id');
                window.history.replaceState({}, '', newUrl);
            }
        };

        if (projectId && session?.access_token) {
            loadProject(projectId);
        }
    }, [projectId, session]);

    const handleSaveProject = async () => {
        if (!session?.access_token) {
            alert("Please log in to save projects");
            return;
        }

        setSaveStatus('saving');
        try {
            const projectData = {
                videos,
                titleHtml,
                titleStyle,
                transitionType,
                transitionDuration,
                audioFileName,
                videoQuality, // Persist video quality setting
                supabase_path: lastGeneratedPath, // Include path for storage tracking
                job_id: currentJobId // Persist the job ID
            };

            // Create a descriptive name
            const projectName = videos.length > 0
                ? `Ranking: ${titleHtml.replace(/<[^>]*>/g, ' ').substring(0, 30)}...`
                : 'Untitled Ranking Video';

            // Get a thumbnail from the first video if available
            const thumbnail = videos[0]?.thumbnail || null;

            if (projectId) {
                // Update
                await ProjectService.updateProject(session.access_token, projectId, {
                    name: projectName,
                    data: projectData,
                    thumbnail_url: thumbnail
                });
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 2000);
            } else {
                // Create
                const newProject = await ProjectService.createProject(
                    session.access_token,
                    'ranking',
                    projectName,
                    projectData
                );
                setProjectId(newProject.id);
                // Update URL without reload
                const newUrl = new URL(window.location);
                newUrl.searchParams.set('project_id', newProject.id);
                window.history.pushState({}, '', newUrl);
            }

            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000);

        } catch (err) {
            console.error(err);
            alert("Failed to save project");
            setSaveStatus('idle');
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleAudioUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAudioFile(file);
            setAudioFileName(file.name);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleRemoveAudio = () => {
        setAudioFile(null);
        setAudioFileName('');
        if (audioInputRef.current) {
            audioInputRef.current.value = '';
        }
    };

    // Load custom fonts from backend
    useEffect(() => {
        const styleId = 'custom-fonts-loader-ranking';
        if (document.getElementById(styleId)) return;

        const style = document.createElement('style');
        style.id = styleId;

        let css = '';
        CUSTOM_FONTS_CONFIG.forEach(font => {
            font.weights.forEach(weight => {
                const filename = `${font.prefix}-${weight}.ttf`;
                css += `
                @font-face {
                    font-family: '${font.family}';
                    src: url('${API_BASE}/api/assets/file/fonts/${filename}') format('truetype');
                    font-weight: ${weight};
                    font-style: normal;
                }
                `;
            });
        });

        style.textContent = css;
        document.head.appendChild(style);
    }, []);

    // Videos State
    const [videos, setVideos] = useState([
        {
            id: 1,
            url: '',
            file: null,
            label: 'Clip 1',
            startTime: 0,
            endTime: 5,
            duration: 10,
            style: { font: 'archivo-black', color: '#06B6D4', strokeColor: '#000000', strokeWidth: 4, strokeOpacity: 100, fontSize: 48, maxWidth: 90, animation: 'popIn' },
            isLoaded: false,
            previewUrl: null
        },
        {
            id: 2,
            url: '',
            file: null,
            label: 'Clip 2',
            startTime: 2,
            endTime: 7,
            duration: 15,
            style: { font: 'archivo-black', color: '#ffffff', strokeColor: '#000000', strokeWidth: 4, strokeOpacity: 100, fontSize: 48, maxWidth: 90, animation: 'slideInRight' },
            isLoaded: false,
            previewUrl: null
        },
    ]);

    const [expandedVideoId, setExpandedVideoId] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generateError, setGenerateError] = useState(null);
    const [generateProgress, setGenerateProgress] = useState(0);
    const [showTimeline, setShowTimeline] = useState(false);
    const [titleExpanded, setTitleExpanded] = useState(false);
    const [transitionsExpanded, setTransitionsExpanded] = useState(false);
    const [audioExpanded, setAudioExpanded] = useState(false);

    const handleVideoChange = (id, newVideoData) => {
        setVideos(videos.map(v => v.id === id ? newVideoData : v));
    };

    const handleAddVideo = () => {
        const newId = Math.max(...videos.map(v => v.id), 0) + 1;
        setVideos([...videos, {
            id: newId,
            url: '',
            file: null,
            label: `Clip ${videos.length + 1}`,
            startTime: 0,
            endTime: 5,
            duration: 30,
            style: { font: 'archivo-black', color: '#ffffff', strokeColor: '#000000', strokeWidth: 4, strokeOpacity: 100, fontSize: 48, maxWidth: 90, animation: 'fadeIn' },
            isLoaded: false,
            previewUrl: null
        }]);
        setExpandedVideoId(newId);
    };

    const handleRemoveVideo = (id) => {
        if (videos.length <= 1) return;
        setVideos(videos.filter(v => v.id !== id));
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(videos);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setVideos(items);
    };

    const handleTitleStyleChange = (key, value) => {
        setTitleStyle(prev => ({ ...prev, [key]: value }));
    };

    const handleTimeUpdate = useCallback((time, duration) => {
        setCurrentTime(time);
        setTotalDuration(duration);
    }, []);

    const handleSeek = useCallback((time) => {
        if (previewRef.current) {
            previewRef.current.seek(time);
        }
    }, []);

    const handleGenerate = async () => {
        const loadedVideos = videos.filter(v => v.isLoaded && (v.url || v.previewUrl));
        if (loadedVideos.length === 0) {
            setGenerateError('Please import at least one video before generating.');
            return;
        }

        setIsGenerating(true);
        setGenerateError(null);
        setGenerateProgress(0);

        try {
            const token = session?.access_token;
            if (!token) {
                throw new Error('Please log in to generate videos');
            }

            // Prepare FormData for multipart upload (to include audio file and video files)
            const formData = new FormData();

            const payload = {
                main_title: titleHtml,
                main_title_html: titleHtml, // Send HTML for rich text rendering
                title_style: titleStyle,
                transition_type: transitionType,
                transition_duration: transitionDuration,
                video_quality: videoQuality, // Pass quality for cache lookup during export
                videos: loadedVideos.map((v, idx) => ({
                    url: v.url || null,
                    label: v.label,
                    startTime: v.startTime,
                    endTime: v.endTime,
                    style: v.style,
                    videoId: v.videoId, // Include video ID for cache lookup
                    previewUrl: v.previewUrl, // Send cached preview URL to backend
                    hasFile: !!v.file,  // Indicate if this video is a file upload
                    fileKey: v.file ? `video_${idx}` : null  // Key to find the file in FormData
                })),
                project_id: projectId // Pass project ID for persistence
            };

            formData.append('config', JSON.stringify(payload));

            // Append video files if any
            loadedVideos.forEach((v, idx) => {
                if (v.file) {
                    formData.append(`video_${idx}`, v.file);
                }
            });

            if (audioFile) {
                formData.append('audio', audioFile);
            }

            // Use fetch with progress tracking (simulated for now)
            const progressInterval = setInterval(() => {
                setGenerateProgress(prev => Math.min(prev + 5, 90));
            }, 500);

            // Track when we started for recovery fallback
            const startTimestamp = Math.floor(Date.now() / 1000);
            let data = null;
            let needsRecovery = false;

            try {
                const response = await fetch(`${API_BASE}/api/ranking/generate`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                });

                clearInterval(progressInterval);

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Failed to generate video');
                }

                // Backend returns job_id for async polling
                const jobId = data.job_id;
                if (!jobId) {
                    throw new Error('No job ID returned from server');
                }



                console.log('Generation started, polling for job:', jobId);
                setCurrentJobId(jobId); // Update state
                setGenerateProgress(30);

                // Auto-save the project with the new job ID
                // Update local state first so the save includes it
                const updatedVideos = loadedVideos; // Or current videos state if needed

                // We need to save the project now so that:
                // 1. It appears in "My Projects"
                // 2. We can track its storage usage later

                // Note: We don't have the final result yet, but we have the job_id
                // We'll pass the job_id to handleSaveProject or update the state then save

                // Call save directly
                if (session?.access_token) {
                    try {
                        const projectData = {
                            videos: videos, // Use current videos state
                            titleHtml,
                            titleStyle,
                            transitionType,
                            transitionDuration,
                            audioFileName,
                            supabase_path: null, // No path yet
                            job_id: jobId, // IMPORTANT: Save the job_id
                            status: 'processing'
                        };

                        const projectName = videos.length > 0
                            ? `Ranking: ${titleHtml.replace(/<[^>]*>/g, ' ').substring(0, 30)}...`
                            : 'Untitled Ranking Video';

                        const thumbnail = videos[0]?.thumbnail || null;

                        if (projectId) {
                            await ProjectService.updateProject(session.access_token, projectId, {
                                name: projectName,
                                data: projectData,
                                thumbnail_url: thumbnail,
                                status: 'processing'
                            });
                        } else {
                            const newProject = await ProjectService.createProject(
                                session.access_token,
                                'ranking',
                                projectName,
                                projectData
                            );
                            setProjectId(newProject.id);
                            // silent URL update
                            const newUrl = new URL(window.location);
                            newUrl.searchParams.set('project_id', newProject.id);
                            window.history.replaceState({}, '', newUrl);
                        }
                        console.log("Auto-saved ranking project with job_id:", jobId);
                    } catch (saveErr) {
                        console.error("Auto-save failed:", saveErr);
                        // Don't fail the generation just because save failed, but warn
                    }
                }

                // Poll for status
                const maxAttempts = 120; // 10 minutes max (120 * 5s)
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    await new Promise(resolve => setTimeout(resolve, 5000)); // 5 second intervals

                    try {
                        const statusResponse = await fetch(`${API_BASE}/api/ranking/status/${jobId}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });

                        const status = await statusResponse.json();
                        console.log(`Poll ${attempt}: status=${status.status}`);

                        // Update progress based on status
                        if (status.message) {
                            if (status.message.includes('Downloading')) setGenerateProgress(40);
                            else if (status.message.includes('Rendering')) setGenerateProgress(60);
                            else if (status.message.includes('Uploading')) setGenerateProgress(85);
                        }

                        if (status.status === 'complete') {
                            setGenerateProgress(100);

                            // Update project_id from auto-save
                            if (status.project_id && !projectId) {
                                setProjectId(status.project_id);
                                const newUrl = new URL(window.location);
                                newUrl.searchParams.set('project_id', status.project_id);
                                window.history.pushState({}, '', newUrl);
                            }

                            // Store supabase_path
                            if (status.supabase_path) {
                                setLastGeneratedPath(status.supabase_path);
                            }
                            if (status.download_url) {
                                setLastDownloadUrl(status.download_url);
                            }

                            // Download the video
                            if (status.download_url) {
                                const fileResponse = await fetch(status.download_url);
                                const blob = await fileResponse.blob();
                                const blobUrl = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = blobUrl;
                                a.download = status.filename || 'ranking_video.mp4';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(blobUrl);
                                document.body.removeChild(a);
                            }

                            // Update credits in UI
                            if (refreshUsage) refreshUsage();
                            return; // Success!
                        }

                        if (status.status === 'error') {
                            throw new Error(status.error || 'Video generation failed');
                        }

                    } catch (pollErr) {
                        console.error(`Poll attempt ${attempt} failed:`, pollErr);
                        // Continue polling unless it's an actual error status
                        if (pollErr.message.includes('generation failed')) {
                            throw pollErr;
                        }
                    }
                }

                throw new Error('Video generation timed out. Please check My Projects.');

            } catch (fetchErr) {
                clearInterval(progressInterval);
                throw fetchErr;
            }

        } catch (err) {
            console.error('Generate error:', err);
            setGenerateError(err.message);
        } finally {
            setIsGenerating(false);
            setGenerateProgress(0);
        }
    };

    return (
        <div className={`video-ranking-page ${showTimeline ? 'timeline-visible' : 'timeline-hidden'}`}>
            <div className="editor-panel">
                <header className="page-header" style={{ padding: '0', background: 'transparent', borderBottom: 'none' }}>
                    <div className="hero" style={{ padding: '0 0 10px', minHeight: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <h1 className="hero-heading" style={{ fontSize: '2.4rem', marginBottom: '0' }}>
                            Ranked
                        </h1>
                        <p className="hero-subtitle" style={{ fontSize: '0.9rem', color: '#ccc', marginBottom: '10px', marginTop: '4px' }}>
                            Create high-retention ranking videos in minutes.
                        </p>
                    </div>
                </header>

                {/* Main Title Section */}
                <div className={`control-group box-group ${titleExpanded ? 'expanded' : 'collapsed'}`}>
                    <div className="group-header clickable" onClick={() => setTitleExpanded(!titleExpanded)}>
                        <Type size={18} />
                        <h3>Main Title</h3>
                        <span className="header-preview">{titleHtml ? 'Rich text' : 'No title'}</span>
                        <button className="collapse-toggle">
                            {titleExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                    </div>
                    {titleExpanded && (
                        <div className="group-content">
                            <RichTextTitleEditor
                                value={titleHtml}
                                onChange={setTitleHtml}
                                titleStyle={titleStyle}
                                onStyleChange={handleTitleStyleChange}
                                placeholder="Enter your title here..."
                            />

                            <div className="control-row">
                                <div className="control-item full-width">
                                    <label>Vertical Position: {titleStyle.verticalPosition}%</label>
                                    <input
                                        type="range"
                                        min="5"
                                        max="45"
                                        value={titleStyle.verticalPosition || 15}
                                        onChange={(e) => handleTitleStyleChange('verticalPosition', parseInt(e.target.value))}
                                        className="cyan-slider"
                                    />
                                    <span className="control-hint">Adjust distance from top</span>
                                </div>
                            </div>

                            <StyleControls
                                values={titleStyle}
                                onChange={handleTitleStyleChange}
                                title="Title Appearance"
                            />
                        </div>
                    )}
                </div>

                {/* Transitions Section */}
                <div className={`control-group box-group ${transitionsExpanded ? 'expanded' : 'collapsed'}`}>
                    <div className="group-header clickable" onClick={() => setTransitionsExpanded(!transitionsExpanded)}>
                        <Sparkles size={18} />
                        <h3>Transitions</h3>
                        <span className="header-preview">{TRANSITION_TYPES[transitionType]?.name || 'Fade'} • {transitionDuration}s</span>
                        <button className="collapse-toggle">
                            {transitionsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                    </div>
                    {transitionsExpanded && (
                        <div className="group-content">
                            <div className="control-row">
                                <div className="control-item full-width">
                                    <label>Transition Effect</label>
                                    <div className="select-wrapper">
                                        <select
                                            value={transitionType}
                                            onChange={(e) => setTransitionType(e.target.value)}
                                        >
                                            {Object.entries(TRANSITION_TYPES).map(([key, value]) => (
                                                <option key={key} value={key}>{value.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="control-row">
                                <div className="control-item full-width">
                                    <label>Duration: {transitionDuration}s</label>
                                    <input
                                        type="range"
                                        min="0.2"
                                        max="2"
                                        step="0.1"
                                        value={transitionDuration}
                                        onChange={(e) => setTransitionDuration(parseFloat(e.target.value))}
                                        className="cyan-slider"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Video & Audio Settings Section */}
                <div className={`control-group box-group ${audioExpanded ? 'expanded' : 'collapsed'}`}>
                    <div className="group-header clickable" onClick={() => setAudioExpanded(!audioExpanded)}>
                        <Settings size={18} />
                        <h3>Video & Audio Settings</h3>
                        <span className="header-preview">{videoQuality}p{audioFileName ? ` • ${audioFileName}` : ''}</span>
                        <button className="collapse-toggle">
                            {audioExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                    </div>
                    {audioExpanded && (
                        <div className="group-content">
                            {/* Video Quality Selector */}
                            <div className="control-row">
                                <div className="control-item full-width">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <label style={{ margin: 0 }}>Video Quality</label>
                                        <div className="tooltip-container">
                                            <Info size={14} className="tooltip-icon" style={{ cursor: 'help' }} />
                                            <div className="tooltip-content" style={{ width: '260px' }}>
                                                Higher quality = longer initial load when adding videos, but instant export since the video is pre-downloaded at your selected quality.
                                                <div className="tooltip-arrow"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="select-wrapper">
                                        <select
                                            value={videoQuality}
                                            onChange={(e) => setVideoQuality(e.target.value)}
                                        >
                                            <option value="480">480p (Fast Load)</option>
                                            <option value="720">720p (Recommended)</option>
                                            <option value="1080">1080p (HD)</option>
                                            <option value="best">Best Available</option>
                                        </select>
                                    </div>
                                    <span className="control-hint">
                                        {videoQuality === '480' && 'Fastest load, suitable for quick previews'}
                                        {videoQuality === '720' && 'Good balance of quality and speed'}
                                        {videoQuality === '1080' && 'High quality, longer initial load'}
                                        {videoQuality === 'best' && 'Maximum quality, may take longer to load'}
                                    </span>
                                </div>
                            </div>

                            {/* Background Music - Coming Soon */}
                            <div className="control-row" style={{ marginTop: '16px' }}>
                                <div className="control-item full-width">
                                    <label>Background Music</label>
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '16px',
                                        color: '#888',
                                        fontStyle: 'italic',
                                        background: 'rgba(255,255,255,0.03)',
                                        borderRadius: '8px',
                                        border: '1px dashed #404040'
                                    }}>
                                        🎵 Feature Coming Soon
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Videos List */}
                <div className="videos-section">
                    <div className="section-header-row">
                        <h3>Videos</h3>
                        <div className="tooltip-container">
                            <Info size={16} className="tooltip-icon" />
                            <div className="tooltip-content">
                                Videos are ranked 5 to 1. #1 is at the Bottom (Best), #5 is at the Top. Playback starts from #5.
                                <div className="tooltip-arrow"></div>
                            </div>
                        </div>
                        <span className="count-badge">{videos.length} clips</span>
                    </div>

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="videos">
                            {(provided) => (
                                <div className="videos-list" {...provided.droppableProps} ref={provided.innerRef}>
                                    {videos.map((video, index) => (
                                        <Draggable key={video.id} draggableId={String(video.id)} index={index}>
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps}>
                                                    <VideoItemCard
                                                        video={video}
                                                        index={index}
                                                        onChange={handleVideoChange}
                                                        onRemove={handleRemoveVideo}
                                                        isExpanded={expandedVideoId === video.id}
                                                        onToggleExpand={() => setExpandedVideoId(expandedVideoId === video.id ? null : video.id)}
                                                        session={session}
                                                        dragHandleProps={provided.dragHandleProps}
                                                        videoQuality={videoQuality}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>

                    <button onClick={handleAddVideo} className="add-more-btn">
                        <Plus size={18} /> Add Comparison
                    </button>
                </div>

                <div className="action-footer">
                    {generateError && (
                        <div className="error-msg" style={{ marginBottom: '12px' }}>{generateError}</div>
                    )}
                    {isGenerating && (
                        <div className="progress-bar-container">
                            <div className="progress-bar-fill" style={{ width: `${generateProgress}%` }} />
                            <span className="progress-text">{generateProgress}% - Processing...</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            className="btn-secondary save-btn"
                            onClick={handleSaveProject}
                            disabled={saveStatus !== 'idle'}
                            title={saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Project'}
                            style={{ width: '48px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0', height: '48px', borderRadius: '12px', background: '#262626', border: '1px solid #404040', color: '#fff', cursor: 'pointer' }}
                        >
                            {saveStatus === 'saving' ? <Loader2 size={24} className="spin" /> : saveStatus === 'saved' ? <Check size={24} /> : <Save size={24} />}
                        </button>

                        <button
                            className="btn-secondary save-btn"
                            onClick={() => setShowYoutubeModal(true)}
                            disabled={!lastGeneratedPath && !lastDownloadUrl}
                            title={lastGeneratedPath || lastDownloadUrl ? "Upload to YouTube" : "Generate video first"}
                            style={{ width: '48px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0', height: '48px', borderRadius: '12px', background: '#262626', border: '1px solid #404040', color: '#fff', cursor: 'pointer' }}
                        >
                            <Youtube size={24} color={lastGeneratedPath || lastDownloadUrl ? "#FF0000" : "currentColor"} />
                        </button>

                        <button
                            className={`generate-btn ${isGenerating ? 'loading' : ''}`}
                            onClick={handleGenerate}
                            disabled={isGenerating}
                            style={{ flex: 2 }}
                        >
                            {isGenerating ? <><Loader2 className="spin" /> Processing...</> : 'GENERATE RANKED'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Preview Panel - Now using Canvas compositor */}
            <div className="preview-panel">
                <PreviewCanvas
                    ref={previewRef}
                    videos={videos}
                    mainTitle={titleHtml}
                    titleStyle={titleStyle}
                    transitionType={transitionType}
                    transitionDuration={transitionDuration}
                    onTimeUpdate={handleTimeUpdate}
                    onClipChange={(index) => {
                        const clip = videos[index];
                        if (clip) setExpandedVideoId(clip.id);
                    }}
                    width={1080}
                    height={1920}
                />
            </div>

            {/* Floating Timeline Toggle (when hidden) */}
            {!showTimeline && (
                <button
                    className="timeline-float-toggle"
                    onClick={() => setShowTimeline(true)}
                >
                    <Layers size={16} />
                    <span>Timeline</span>
                    <ChevronUp size={16} />
                </button>
            )}

            {/* Composite Timeline - Full width at bottom */}
            {showTimeline && (
                <div className="timeline-panel">
                    <div className="timeline-toggle-header">
                        <button
                            className="timeline-toggle-btn"
                            onClick={() => setShowTimeline(false)}
                        >
                            <Layers size={16} />
                            <span>Hide Timeline</span>
                            <ChevronDown size={16} />
                        </button>
                    </div>
                    <CompositeTimeline
                        clips={videos}
                        currentTime={currentTime}
                        totalDuration={totalDuration}
                        transitionDuration={transitionDuration}
                        transitionType={transitionType}
                        onSeek={handleSeek}
                        onClipSelect={(id) => setExpandedVideoId(id)}
                        onTransitionChange={setTransitionType}
                        selectedClipId={expandedVideoId}
                        audioTrack={audioFile ? { name: audioFileName } : null}
                    />
                </div>
            )}

            <YouTubeUploadModal
                isOpen={showYoutubeModal}
                onClose={() => setShowYoutubeModal(false)}
                session={session}
                project={{
                    id: projectId || `ranking_${currentJobId}`,
                    name: titleHtml ? `Ranking: ${titleHtml.replace(/<[^>]*>/g, ' ')}` : 'Ranking Video',
                    output_url: lastDownloadUrl,
                    data: {
                        supabase_path: lastGeneratedPath,
                        script: ''
                    }
                }}
            />
        </div>
    );
};
